import { Injectable } from '@angular/core';
import { User } from '../user/user';
import { UserService } from '../user/user.service';
import * as api from '../../api';
import * as Rx from 'rxjs';
import { OrgUnitModel } from '../../api/model/orgUnitModel';

export interface Facility {
    id: string;
    name: string;
    isFacility: boolean,
    selected?: boolean;
    billCutOffDate?: number;
    isPerDiem?: boolean;
    isInvoiceApproval?: boolean;
    isIpAllowedToAccessFacility?: boolean;
    unitType?: string;
    companyName?: string;
    divisionName?: string;
    regionName?: string;
    indentLevel?: number;
}

//export interface Region {
//    id: string;
//    name: string;
//    facilities: Facility[];
//}

/**
 * Service for information facilities & regions.
 */
@Injectable()
export class FacilitiesService {

    public static readonly CompanyType = 'Company';
    public static readonly DivisionType = 'Division';
    public static readonly RegionType = 'Region';
    public static readonly FacilityType = 'Facility';
    //Set to 1 if displaying company root record
    private indentOffset : number = 0;

    /**
     * Subject to observe to witness changes to the facilities available.
     * Converted from array of Facility so that we don't reference it before it is done loading.
     */
    private _allowedFacilities$ = new Rx.BehaviorSubject<Facility[]>([]);

    /** All of the user's Regions, with their Facilities */
    //public readonly regions = new Array<Region>();

    /**
     * Subject to observe to witness changes to the facilities available.
     * This service emits to this observable.
     */
    public readonly allFacilities$ = new Rx.BehaviorSubject<Facility[]>([]);

    /**
     * Subject to observe to witness changes to the facility selections.
     * Call .next() to change the current selections.
     * This service does *not* emit to this observable. That responsibility falls upon GlobalFacilitySelectorComponent.
     * ResidentsLandingComponent will also emit to this in order to dictate that only one facility is selected.
     */
    public readonly selectedFacilities$ = new Rx.BehaviorSubject<Facility[]>([]);

    /**
     * Subject to observe when a compnent requires a single selected facility
     * Call .next() to change the required values
     * This service does *not* emit to this observable.
     */

    public readonly singleSelectedFacilityPage$ = new Rx.BehaviorSubject<boolean>(false);

    public get allowedFacilities(): Facility[]{
        return this._allowedFacilities$.getValue();
    }

    /**
     * The selected facilities as an array of IDs, as needed for API calls.
     * @returns {string[]}
     */
    get selectedAsOrgList(): Array<string> {
        return this.selectedFacilities$.getValue().map(f => f.id);
    }

    get billCutOffDateForSelectedFacility(): number {
        let facsBillDate =  this.selectedFacilities$.getValue().map(f => f.billCutOffDate)
        return Math.max.apply(null,facsBillDate);
    }

    get perDiemStatusForSelectedFacilities(): boolean {
        let perDiemStatuses = this.selectedFacilities$.getValue().map(f => f.isPerDiem);
        let isPerDiem = perDiemStatuses.some(status => status == true);
        return isPerDiem
    }

    get showInvoiceApprovalForSelectedFacilities(): boolean {
      let invoiceApprovalStatuses = this.selectedFacilities$.getValue().map(f => f.isInvoiceApproval);
      let showInvoiceApproval = invoiceApprovalStatuses.some(status => status == true);

      return showInvoiceApproval;
    }

    /** Construct */
    constructor(
        private userSvc: UserService,
        private userApi: api.UserService
    ) {
        this.userSvc.user$.subscribe({
            next: (user) => this.onUserLoginChanged(user),
            error: (error) => console.error(error)});

        //When impersonating we show companies so indent everything else an extra level            
        if (userSvc.isImpersonating) {
            this.indentOffset = 1;
        }
    }

    /**
     * React to change in user authentication state by loading / clearing state.
     * @param user the new authenticated User, or null upon logout.
     */
    private onUserLoginChanged(user: User|null): void {
        //this.regions.length = 0;

        if (!user) {
            // logout
            this.allFacilities$.next([] as Facility[]);
        }
        else if (user.jwt && user.username) {
            this.userApi.userGetUserUnits(user.username)
                .subscribe((orgUnits: Array<OrgUnitModel>) => this.processOrgUnitList(orgUnits));
        }
    }

    /**
     * New OrgUnits received from the backend - process 'em.
     */
    private processOrgUnitList(orgUnits: OrgUnitModel[]) {
        //this.regions.length = 0;
        let facilities = orgUnits.filter(x=> this.userSvc.isImpersonating || x.UnitType !== FacilitiesService.CompanyType)
            .map(ou => ({
                id: ou.Id, 
                name: ou.Name, 
                isFacility: ou.IsFacility,
                selected: true, 
                billCutOffDate: ou.BillCutOffDate, 
                isPerDiem: ou.IsPerDiem, 
                isInvoiceApproval: ou.IsInvoiceApproval,
                isIpAllowedToAccessFacility: ou.IsIpAllowedToAccessFacility,
                unitType: ou.UnitType,
                companyName: ou.CompanyName || '',
                divisionName: ou.DivisionName || '',
                regionName: ou.RegionName || '',
                indentLevel: FacilitiesService.getIndentLevel(ou, this.indentOffset),
            } as Facility));
        
        this._allowedFacilities$.next(facilities.filter(f => f.isIpAllowedToAccessFacility));

        this.allFacilities$.next(facilities);
    }

    /**
     * Returns an array of Facility objects from OrgUnits for display in UI
     * @param selectedFacilities 
     * @param possibleFacilities 
     */
    public convertOrgUnitsToFacilities(selectedFacilities: OrgUnitModel[], possibleFacilities: OrgUnitModel[]): Facility[]{
        let array: Facility[] = [];
        for (let currentUnit of possibleFacilities.filter(x => this.userSvc.isImpersonating|| x.UnitType !== FacilitiesService.CompanyType)) {
            array.push({
                id: currentUnit.Id,
                name: currentUnit.Name,
                isFacility: currentUnit.IsFacility,
                companyName: currentUnit.CompanyName,
                divisionName: currentUnit.DivisionName,
                regionName: currentUnit.RegionName,
                unitType: currentUnit.UnitType,
                indentLevel: FacilitiesService.getIndentLevel(currentUnit, this.indentOffset),
                selected: FacilitiesService.findOrgUnitInArray(currentUnit, selectedFacilities)
            });
        }
        return array;
    }

    private static findOrgUnitInArray(toFind: OrgUnitModel, array: OrgUnitModel[]): boolean {
        for(let f of array){
            if(toFind.Id===f.Id){
                return true;
            }
        }
        return false;
    }

    //If including company add 1 to the result...
    private static getIndentLevel(unit: OrgUnitModel, offset: number) : number {
        // Indent Logic - if showing company we indent by 1 extra unit
        // Div Reg Fac Indent
        //         x   0
        // x           0
        // x       x   1
        // x   x       1
        // x   x   x   2

        //Offset is 1 if we are impersonating and including Company level
        let indent : number = 0;
        switch (unit.UnitType) {
            case FacilitiesService.DivisionType : 
                indent = offset;
                break;
            case FacilitiesService.RegionType :
                indent = 1 + offset;
                break;
            case FacilitiesService.FacilityType :
                if (unit.RegionName) {
                    indent = 2 + offset; 
                } else {
                    if (unit.DivisionName) {
                        indent = 1 + offset;
                    }
                }
        }
        return indent;
    }

    public setParentsAndChildren(available: Facility[], facility: Facility) {
        //If this is a node within a custom org - see if we need to check/uncheck children
        switch(facility.unitType) {
            case FacilitiesService.RegionType: {
                //If selected is top level node, update all children of selected region
                available.forEach(x => {
                    if (facility.companyName === x.companyName && facility.divisionName === x.divisionName && facility.regionName === x.regionName) {
                        x.selected = facility.selected;
                    }
                });
                break;
            }

            case FacilitiesService.DivisionType: {
                //If selected is top level node, update all children of selected division
                available.forEach(x => {
                    if (facility.companyName === x.companyName && facility.divisionName === x.divisionName) {
                        x.selected = facility.selected;
                    }
                });
                break;
            }

            case FacilitiesService.CompanyType: {
                //If selected is top level node, update all children
                available.forEach(x => {
                    if (facility.companyName === x.companyName) {
                        x.selected = facility.selected;
                    }
                });
                break;
            }
        }

        //Now check if all the facilities are checked/unchecked then so should the parents

        //Check the region
        let region = available.find(x => x.companyName === facility.companyName 
            && x.divisionName === facility.divisionName
            && x.regionName === facility.regionName
            && x.unitType === FacilitiesService.RegionType);
        if (region) {
            region.selected = !available.some(x => x.companyName === facility.companyName 
                && x.divisionName === facility.divisionName
                && x.regionName === facility.regionName
                && x.isFacility 
                && !x.selected);
        }

        //Check the division
        let division = available.find(x => x.companyName === facility.companyName 
            && x.divisionName === facility.divisionName
            && x.unitType === FacilitiesService.DivisionType);
        if (division) {
            division.selected = !available.some(x => x.companyName === facility.companyName 
                && x.divisionName === facility.divisionName
                && x.isFacility 
                && !x.selected);
        }

        //Check the company
        let company = available.find(x => x.companyName === facility.companyName 
            && x.unitType === FacilitiesService.CompanyType);
        if (company) {
            company.selected = !available.some(x => x.companyName === facility.companyName 
                && x.isFacility 
                && !x.selected);
        }        
    }
}
